import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { KlaraDocument } from '../../../model/klara';
import { useAppTranslation } from '../../../services/i18n';
import { formatSize } from '../format';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

/**
 * How far the picture may be dragged: only as far as the zoom pushes it past
 * the frame, so no drag can open a gap. Measured on the painted picture, not
 * on the box — `contain` letterboxes a wide illustration in a tall frame, and
 * measuring the box would let it be dragged out of sight.
 */
const clampTo = (
  next: { x: number; y: number },
  zoom: number,
  box: HTMLImageElement | null
) => {
  if (!box) return { x: 0, y: 0 };
  const fit =
    box.naturalWidth && box.naturalHeight
      ? Math.min(box.clientWidth / box.naturalWidth, box.clientHeight / box.naturalHeight)
      : 0;
  const painted = fit
    ? { width: box.naturalWidth * fit, height: box.naturalHeight * fit }
    : { width: box.clientWidth, height: box.clientHeight };
  const limitX = Math.max(0, (painted.width * zoom - box.clientWidth) / 2);
  const limitY = Math.max(0, (painted.height * zoom - box.clientHeight) / 2);
  return {
    x: Math.min(limitX, Math.max(-limitX, next.x)),
    y: Math.min(limitY, Math.max(-limitY, next.y)),
  };
};

type Props = {
  images: KlaraDocument[];
  /** Index of the open image; null keeps the lightbox closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  downloadUrl: (document: KlaraDocument) => string;
};

/**
 * Full-screen viewer for the generated illustrations.
 *
 * Ours, not the design's — the frame draws the grid and stops there, and a
 * click used to start a download, which is no way to look at a picture. The
 * download stays, but as a button in here rather than as the only thing a
 * click can do.
 */
export function ImageLightbox({ images, index, onIndexChange, onClose, downloadUrl }: Props) {
  const t = useAppTranslation();
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Where the pointer grabbed the image, and the offset it had at that moment.
  const drag = useRef<{ x: number; y: number; offset: { x: number; y: number } } | null>(null);
  const picture = useRef<HTMLImageElement | null>(null);

  const image = index === null ? undefined : images[index];

  // A new picture starts unzoomed, or the previous one's pan would carry over.
  useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [index]);

  const step = useCallback(
    (delta: number) => {
      if (index === null || images.length < 2) return;
      onIndexChange((index + delta + images.length) % images.length);
    },
    [index, images.length, onIndexChange]
  );

  // Computed from the previous zoom rather than from a rendered value: a
  // trackpad sends many wheel events per frame, and they all have to count.
  const applyZoom = useCallback((next: (current: number) => number) => {
    setZoom((current) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next(current))));
  }, []);

  const changeZoom = useCallback(
    (delta: number) => applyZoom((current) => current + delta),
    [applyZoom]
  );

  // Double click and wheel do what they do in any viewer: in, out, back to fit.
  const toggleZoom = () =>
    applyZoom((current) =>
      current > MIN_ZOOM ? MIN_ZOOM : MIN_ZOOM + ZOOM_STEP * 2
    );

  // The pan follows the zoom, whichever way the zoom was asked for.
  useEffect(() => {
    setOffset((current) => clampTo(current, zoom, picture.current));
  }, [zoom]);

  // Keyed off the picture, not the index: an index left pointing past a
  // shrunken gallery renders nothing, and must not keep swallowing keys.
  useEffect(() => {
    if (!image) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') step(-1);
      else if (event.key === 'ArrowRight') step(1);
      else if (event.key === '+' || event.key === '=') changeZoom(ZOOM_STEP);
      else if (event.key === '-') changeZoom(-ZOOM_STEP);
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [image, step, changeZoom]);

  if (!image) return null;

  const caption = [
    typeof image.metadata.section === 'string'
      ? t('klara.outputs.section', { section: image.metadata.section })
      : null,
    formatSize(image.size),
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      // The picture is the point, so the sheet gives it the whole window bar a
      // margin, and the chrome sits on the dark ground with it.
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            backgroundImage: 'none',
            width: 'calc(100vw - 48px)',
            height: 'calc(100vh - 48px)',
            maxWidth: 'none',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px 10px 20px',
          color: 'var(--color-white)',
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {image.file_name ?? t('klara.outputs.untitledImage')}
          </Typography>
          <Typography sx={{ fontSize: '12px', opacity: 0.7 }}>
            {[
              t('klara.outputs.imagePosition', {
                index: (index ?? 0) + 1,
                total: images.length,
              }),
              caption,
            ]
              .filter(Boolean)
              .join(' • ')}
          </Typography>
        </Box>

        <LightboxButton
          title={t('klara.outputs.zoomOut')}
          onClick={() => changeZoom(-ZOOM_STEP)}
          disabled={zoom <= MIN_ZOOM}
        >
          <ZoomOutIcon />
        </LightboxButton>
        <LightboxButton
          title={t('klara.outputs.zoomIn')}
          onClick={() => changeZoom(ZOOM_STEP)}
          disabled={zoom >= MAX_ZOOM}
        >
          <ZoomInIcon />
        </LightboxButton>
        {image.available && (
          <LightboxButton title={t('klara.outputs.download')} href={downloadUrl(image)}>
            <FileDownloadOutlinedIcon />
          </LightboxButton>
        )}
        <LightboxButton title={t('klara.outputs.closeImage')} onClick={onClose}>
          <CloseIcon />
        </LightboxButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '0 12px 16px',
        }}
      >
        {images.length > 1 && (
          <LightboxButton title={t('klara.outputs.previousImage')} onClick={() => step(-1)}>
            <ChevronLeftIcon />
          </LightboxButton>
        )}

        <Box
          // Panning happens here rather than on the image, so a pointer that
          // leaves the picture mid-drag still moves it.
          onPointerDown={(event) => {
            if (zoom <= MIN_ZOOM) return;
            drag.current = { x: event.clientX, y: event.clientY, offset };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const start = drag.current;
            if (!start) return;
            setOffset(
              clampTo(
                {
                  x: start.offset.x + (event.clientX - start.x),
                  y: start.offset.y + (event.clientY - start.y),
                },
                zoom,
                picture.current
              )
            );
          }}
          onPointerUp={() => {
            drag.current = null;
          }}
          // A gesture the browser or the OS takes over never sends pointerup,
          // and a stale origin would jump the picture on the next move.
          onPointerCancel={() => {
            drag.current = null;
          }}
          onLostPointerCapture={() => {
            drag.current = null;
          }}
          onDoubleClick={toggleZoom}
          onWheel={(event) => changeZoom(event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            cursor: zoom > MIN_ZOOM ? 'grab' : 'default',
            '&:active': { cursor: zoom > MIN_ZOOM ? 'grabbing' : 'default' },
          }}
        >
          <Box
            component="img"
            ref={picture}
            src={downloadUrl(image)}
            alt={image.file_name ?? ''}
            draggable={false}
            sx={{
              // Fills the sheet rather than sitting at its own pixel size:
              // a 400px chapter illustration is unreadable at 1:1 on a big
              // screen, and contain keeps its proportions either way.
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              transition: drag.current ? 'none' : 'transform 120ms ease-out',
            }}
          />
        </Box>

        {images.length > 1 && (
          <LightboxButton title={t('klara.outputs.nextImage')} onClick={() => step(1)}>
            <ChevronRightIcon />
          </LightboxButton>
        )}
      </Box>
    </Dialog>
  );
}

function LightboxButton({
  title,
  onClick,
  href,
  disabled,
  children,
}: {
  title: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          aria-label={title}
          onClick={onClick}
          // The download is a link, so it keeps the filename the server sends.
          {...(href ? { component: 'a' as const, href } : {})}
          disabled={disabled}
          sx={{
            color: 'var(--color-white)',
            '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}
